import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  MENTORUM_RISK_KAPANIS_NEDENLERI,
  mentorumRiskKapatSemasi,
  type MentorumRiskKapanisNedeni,
} from "@koclukpanel/contracts";
import { Buton, Kart, Rozet, Secim } from "@koclukpanel/ui";
import { api } from "./api";
import { Hata403 } from "./SistemSayfalari";
import { izinSorgusu, type IzinHaritasi } from "./izin";
import { MENTORUM_OKUMA, MentorumHata, mentorumTarih } from "./MentorumYonetim";

const KAPANIS_ETIKETLERI: Record<MentorumRiskKapanisNedeni, string> = {
  STUDENT_CONTACTED: "Öğrenciyle görüşüldü",
  PARENT_CONTACTED: "Veliyle görüşüldü",
  REFERRED_TO_SPECIALIST: "Uzmana yönlendirildi",
  FALSE_ALARM: "Yanlış alarm",
};
const KURAL_ETIKETLERI: Record<string, string> = {
  MOOD_PATTERN: "Duygu uyarısı",
  KEYWORD: "Kelime taraması uyarısı",
  RISKY_OPTION: "Riskli seçenek uyarısı",
};
interface RiskUyarisi {
  id: string;
  studentId: string;
  rule: string;
  createdAt: string;
  lastTriggeredAt: string;
  triggerCount: number;
  handledAt: string | null;
  handledReason: MentorumRiskKapanisNedeni | null;
  gecikmis: boolean;
}

export function MentorumRisk({
  izinler,
  onKayitAc,
}: {
  izinler: IzinHaritasi;
  onKayitAc: (ogrenciId: string) => void;
}) {
  const izinVar = izinSorgusu(izinler);
  const izinli = izinVar("mentorum.risk.goruntule");
  const liste = useQuery({
    queryKey: ["mentorum", "risk"],
    queryFn: ({ signal }) => api<RiskUyarisi[]>("/mentorum/risk-uyarilari", { signal }),
    enabled: izinli,
    ...MENTORUM_OKUMA,
  });
  if (!izinli) return <Hata403 onBugun={() => window.location.assign("/")} />;
  return (
    <div className="flex min-w-0 flex-col gap-4 break-words">
      <h2 className="text-notr-900 text-xl font-semibold">Mentörüm risk uyarıları</h2>
      <Kart ton="uyari">
        <p>Otomatik tarama her şeyi yakalayamaz.</p>
      </Kart>
      <Buton gorunum="ikincil" disabled={liste.isFetching} onClick={() => void liste.refetch()}>
        Uyarıları yenile
      </Buton>
      {liste.isPending ? (
        <p role="status">Uyarılar yükleniyor…</p>
      ) : liste.isError ? (
        <MentorumHata hata={liste.error} />
      ) : liste.data.length === 0 ? (
        <p>Risk uyarısı bulunmuyor.</p>
      ) : (
        liste.data.map((uyari) => (
          <UyariSatiri
            key={uyari.id}
            uyari={uyari}
            onKayitAc={
              izinVar("mentorum.kayit.goruntule") ? () => onKayitAc(uyari.studentId) : undefined
            }
            tazele={() => void liste.refetch()}
          />
        ))
      )}
    </div>
  );
}

function UyariSatiri({
  uyari,
  onKayitAc,
  tazele,
}: {
  uyari: RiskUyarisi;
  onKayitAc?: () => void;
  tazele: () => void;
}) {
  const [sebep, setSebep] = useState("");
  const kapat = useMutation({
    gcTime: 0,
    mutationFn: () =>
      api(`/mentorum/risk-uyarilari/${encodeURIComponent(uyari.id)}`, {
        yontem: "PATCH",
        govde: mentorumRiskKapatSemasi.parse({ handledReason: sebep }),
      }),
    onSuccess: tazele,
  });
  // F-MNT-14/M11 — sıralama ve 24 saat kararı sunucudan gelir; satır kayıt içeriği taşımaz.
  return (
    <Kart baslik={KURAL_ETIKETLERI[uyari.rule] ?? "Risk uyarısı"}>
      <div className="flex min-w-0 flex-col gap-3">
        <p className="break-all">Öğrenci: {uyari.studentId}</p>
        <p>Son tetiklenme: {mentorumTarih(uyari.lastTriggeredAt)}</p>
        <p>Tetiklenme sayısı: {uyari.triggerCount}</p>
        <div className="flex flex-wrap gap-2">
          <Rozet>{uyari.handledAt ? "Ele alındı" : "Açık"}</Rozet>
          {uyari.gecikmis && <Rozet>Gecikmiş</Rozet>}
        </div>
        {onKayitAc && (
          <Buton gorunum="ikincil" onClick={onKayitAc}>
            Kayıt detayını aç
          </Buton>
        )}
        {uyari.handledAt ? (
          <p>
            {uyari.handledReason ? KAPANIS_ETIKETLERI[uyari.handledReason] : "Ele alındı"} ·{" "}
            {mentorumTarih(uyari.handledAt)}
          </p>
        ) : (
          <form
            className="flex min-w-0 flex-col gap-3"
            onSubmit={(olay) => {
              olay.preventDefault();
              if (mentorumRiskKapatSemasi.safeParse({ handledReason: sebep }).success)
                kapat.mutate();
            }}
          >
            <Secim
              etiket="Kapanış sebebi"
              zorunlu
              value={sebep}
              onChange={(olay) => setSebep(olay.target.value)}
            >
              <option value="">Sebep seçin</option>
              {MENTORUM_RISK_KAPANIS_NEDENLERI.map((neden) => (
                <option key={neden} value={neden}>
                  {KAPANIS_ETIKETLERI[neden]}
                </option>
              ))}
            </Secim>
            <MentorumHata hata={kapat.error} />
            <Buton type="submit" disabled={!sebep || kapat.isPending}>
              {kapat.isPending ? "Kapatılıyor…" : "Ele alındı olarak kapat"}
            </Buton>
          </form>
        )}
      </div>
    </Kart>
  );
}
